// Question schemas — plan §5.4 + §11.2 + Ek A.
// `options` JSONB tipe göre discriminated union. Value shape validation M4'te.
//
// ⚠️ KİT SENKRONU: Bu dosyadaki şemalar docs/framework-kit/framework.schema.json'a
// (Framework Yazım Kiti) yansır. Tip/alan/kısıt değişince `pnpm kit:schema` + kılavuz + sürüm bump.
// `.describe()` metinleri müşteriye giden JSON Schema'da `description` olur.

import { z } from "zod";
import { ApplicablePeriodsSchema, PeriodType } from "./period.js";

export const QuestionType = z
  .enum([
    "text",
    "numeric",
    "single",
    "multi",
    "date",
    "file",
    "table",
    "percent",
    "repeater",
    "composite",
  ])
  .describe("10 soru tipi. composite parçaları composite olamaz (derinlik 1).");
export type QuestionType = z.infer<typeof QuestionType>;

// Ortak alt yapı — seçenek listesi (single/multi/percent).
// `allowCustomInput` sadece single/multi için anlamlı (M5.5): opsiyon seçilince
// kullanıcıya serbest metin alanı açar ("Diğer, açıklayınız" pattern'i). Percent'te
// ignore edilir. Custom text form_responses.value içinde customInput(s) olarak yaşar.
export const OptionItemSchema = z
  .object({
    id: z
      .string()
      .min(1)
      .describe("Seçenek kimliği: kısa, anlamlı, stabil (örn. opt_evet). Yıl/dönem gömülmez."),
    label: z.string().min(1).describe("Seçeneğin görünen etiketi."),
    allowCustomInput: z
      .boolean()
      .default(false)
      .describe('Seçilince serbest metin alanı açar ("Diğer, açıklayınız"). Yalnız single/multi.'),
  })
  .describe("Seçim listesi öğesi (single/multi/percent).");
export type OptionItem = z.infer<typeof OptionItemSchema>;

// Custom input karakter limiti (M5.5 karar — 1000)
export const CUSTOM_INPUT_MAX_LENGTH = 1000;

export const TableColumnSchema = z
  .object({
    id: z.string().min(1).describe("Sütun kimliği: kısa, anlamlı (örn. col_kaynak). Yıl gömülmez."),
    label: z.string().min(1).describe("Sütun başlığı."),
    type: z.enum(["text", "numeric", "date"]).describe("Hücre tipi."),
    // #006: numeric sütunlarda birim metadata'sı (ton, MWh, %, vs.). AI prompt'a ipucu olarak yansır.
    unit: z
      .string()
      .max(40)
      .optional()
      .describe("Sayısal sütun birimi (ton, MWh, %); ≤ 40 karakter."),
  })
  .describe("Tablo sütunu.");
export type TableColumn = z.infer<typeof TableColumnSchema>;

export const RepeaterSubFieldSchema = z
  .object({
    id: z.string().min(1).describe("Alt alan kimliği (örn. f_ad)."),
    label: z.string().min(1).describe("Alt alan etiketi."),
    type: z.enum(["text", "numeric", "date"]).describe("Alt alan tipi."),
  })
  .describe("Tekrarlayan satırın bir alt alanı.");
export type RepeaterSubField = z.infer<typeof RepeaterSubFieldSchema>;

// 9 temel varyant — composite parçalarının options'ı da bu union'dan gelir.
// Composite'in kendisi burada YOK → "composite içinde composite" şemadan imkansız (derinlik 1, M4).
export const BaseQuestionOptionsSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("text"),
      maxLength: z
        .number()
        .int()
        .positive()
        .max(50_000)
        .default(5000)
        .describe("Karakter sınırı, ≤ 50000."),
    })
    .describe("Serbest metin / anlatı."),
  z
    .object({
      type: z.literal("numeric"),
      unit: z.string().optional().describe("Birim (tCO₂e, MWh, %)."),
      min: z.number().optional(),
      max: z.number().optional(),
    })
    .describe("Tek sayı + isteğe bağlı birim/min/max (KPI)."),
  z
    .object({
      type: z.literal("single"),
      choices: z.array(OptionItemSchema).min(1),
    })
    .describe("Tek seçim; choices ≥ 1. Evet/Hayır + açıklama için allowCustomInput."),
  z
    .object({
      type: z.literal("multi"),
      choices: z.array(OptionItemSchema).min(1),
    })
    .describe("Çoklu seçim; choices ≥ 1."),
  z
    .object({
      type: z.literal("date"),
      format: z.enum(["YYYY-MM-DD", "MM-YYYY"]).default("YYYY-MM-DD"),
    })
    .describe("Tarih veya ay-yıl (dönem)."),
  z
    .object({
      type: z.literal("file"),
      acceptedMimeTypes: z.array(z.string()).default([]).describe("Boş = hepsi."),
      maxSizeMb: z.number().int().positive().max(1024).default(20),
    })
    .describe("Dosya yükleme (belge). Kanıt için genelde allowEvidence tercih edilir."),
  z
    .object({
      type: z.literal("table"),
      columns: z.array(TableColumnSchema).min(1),
    })
    .describe(
      "Tablo; columns ≥ 1. Rapordaki satır×sütun tablolar için. Aktarım uyumu için ≤ 5 sütun, her sütun kısa değer (ad, tarih, tutar, oran, durum) — anlatı isteyen sütun açma.",
    ),
  z
    .object({
      type: z.literal("percent"),
      categories: z.array(OptionItemSchema).min(1),
    })
    .describe("Toplamı %100 eden dağılım; categories ≥ 1."),
  z
    .object({
      type: z.literal("repeater"),
      subFields: z.array(RepeaterSubFieldSchema).min(1),
    })
    .describe(
      "Aynı yapıda tekrar eden kalemler (projeler, politikalar); subFields ≥ 1. Aktarım uyumu için ≤ 4 alan, her alan kısa değer (ad, tarih, tutar, oran, durum) — anlatı gerekiyorsa ayrı bir text sorusu aç.",
    ),
]);
export type BaseQuestionOptions = z.infer<typeof BaseQuestionOptionsSchema>;

// Composite parça — key value JSONB anahtarı olur; export/import code-tabanlı eşleşme için stabil kalmalı.
export const COMPOSITE_PART_KEY_RE = /^[a-z0-9_]+$/;
export const CompositePartSchema = z
  .object({
    key: z
      .string()
      .min(1)
      .max(40)
      .regex(COMPOSITE_PART_KEY_RE, "Parça kodu yalnızca küçük harf, rakam ve _ içerebilir")
      .describe("Parça anahtarı ^[a-z0-9_]+$, ≤ 40; değer JSON'unda anahtar olur, stabil."),
    label: z.string().min(1).describe("Parça etiketi."),
    helperText: z.string().optional(),
    required: z
      .boolean()
      .default(false)
      .describe("Parça zorunlu mu; sorunun required'ı bunlardan türetilir."),
    options: BaseQuestionOptionsSchema.describe("Parçanın tipi ve ayarları (composite OLAMAZ)."),
  })
  .describe("Bileşik sorunun bir parçası.");
export type CompositePart = z.infer<typeof CompositePartSchema>;

export const COMPOSITE_MAX_PARTS = 10;

// Discriminated union — options shape her tip için farklı.
// superRefine: mükerrer parça key yok + en fazla 1 dosya parçası (M2 — response_attachments değişmez).
export const QuestionOptionsSchema = z
  .discriminatedUnion("type", [
    ...BaseQuestionOptionsSchema.options,
    z
      .object({
        type: z.literal("composite"),
        parts: z.array(CompositePartSchema).min(1).max(COMPOSITE_MAX_PARTS),
      })
      .describe(
        "Bileşik: 1-10 parça, en fazla 1 dosya parçası, derinlik 1. Birbirine bağlı alanlar için.",
      ),
  ])
  .superRefine((opts, ctx) => {
    if (opts.type !== "composite") return;
    const seen = new Set<string>();
    opts.parts.forEach((p, i) => {
      if (seen.has(p.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["parts", i, "key"],
          message: `Mükerrer parça kodu: "${p.key}"`,
        });
      }
      seen.add(p.key);
    });
    const fileCount = opts.parts.filter((p) => p.options.type === "file").length;
    if (fileCount > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["parts"],
        message: "Bileşik soruda en fazla bir dosya parçası olabilir",
      });
    }
  });
export type QuestionOptions = z.infer<typeof QuestionOptionsSchema>;
export type CompositeQuestionOptions = Extract<QuestionOptions, { type: "composite" }>;

// D15 yardımcısı — composite + dosya parçası var mı? (allowEvidence çakışma kontrolü)
export function compositeHasFilePart(options: QuestionOptions): boolean {
  return options.type === "composite" && options.parts.some((p) => p.options.type === "file");
}

// Tipe göre varsayılan options üretici (FE'de "yeni soru" başlangıcı için)
export function defaultOptionsForType(type: QuestionType): QuestionOptions {
  switch (type) {
    case "text":
      return { type: "text", maxLength: 5000 };
    case "numeric":
      return { type: "numeric" };
    case "single":
      return {
        type: "single",
        choices: [{ id: "opt1", label: "Seçenek 1", allowCustomInput: false }],
      };
    case "multi":
      return {
        type: "multi",
        choices: [{ id: "opt1", label: "Seçenek 1", allowCustomInput: false }],
      };
    case "date":
      return { type: "date", format: "YYYY-MM-DD" };
    case "file":
      return { type: "file", acceptedMimeTypes: [], maxSizeMb: 20 };
    case "table":
      return {
        type: "table",
        columns: [{ id: "c1", label: "Sütun 1", type: "text" }],
      };
    case "percent":
      return {
        type: "percent",
        categories: [{ id: "cat1", label: "Kategori 1", allowCustomInput: false }],
      };
    case "repeater":
      return {
        type: "repeater",
        subFields: [{ id: "f1", label: "Alan 1", type: "text" }],
      };
    case "composite":
      return {
        type: "composite",
        parts: [
          {
            key: "alan1",
            label: "Alan 1",
            required: false,
            options: { type: "text", maxLength: 5000 },
          },
        ],
      };
  }
}

// Question entity (response shape)
export const QuestionSchema = z.object({
  id: z.string().uuid(),
  sectionId: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  helperText: z.string().nullable(),
  type: QuestionType,
  options: QuestionOptionsSchema,
  required: z.boolean(),
  enabled: z.boolean(),
  priorYearHintDisabled: z.boolean(),
  allowEvidence: z.boolean(),
  allowNote: z.boolean(),
  aiContext: z.string().nullable(),
  order: z.number().int(),
  // Raporlama dönemi (K7): soruda TERCİH — DB her zaman en az bir dönem tutar (default hepsi).
  applicablePeriods: z.array(PeriodType),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Question = z.infer<typeof QuestionSchema>;

// Create request — versiyon seviyesinde updated_at ile optimistic lock.
// D15: dosya parçası içeren composite'te allowEvidence açılamaz (aynı attachment havuzu çakışır).
export const CreateQuestionRequestSchema = z
  .object({
    expectedVersionUpdatedAt: z.string().datetime(),
    code: z.string().min(1, "Kod boş olamaz"),
    label: z.string().min(1, "Etiket boş olamaz"),
    description: z.string().optional(),
    helperText: z.string().optional(),
    type: QuestionType,
    options: QuestionOptionsSchema,
    required: z.boolean().default(false),
    enabled: z.boolean().default(true),
    priorYearHintDisabled: z.boolean().default(false),
    allowEvidence: z.boolean().default(false),
    allowNote: z.boolean().default(false),
    aiContext: z.string().optional(),
    order: z.number().int().optional(),
    applicablePeriods: ApplicablePeriodsSchema.optional(),
  })
  .superRefine((q, ctx) => {
    if (q.allowEvidence && compositeHasFilePart(q.options)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["allowEvidence"],
        message: "Dosya parçası içeren bileşik soruda kanıt belgesi açılamaz",
      });
    }
  });
export type CreateQuestionRequest = z.input<typeof CreateQuestionRequestSchema>;

export const UpdateQuestionRequestSchema = z
  .object({
    expectedVersionUpdatedAt: z.string().datetime(),
    code: z.string().min(1).optional(),
    label: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    helperText: z.string().nullable().optional(),
    type: QuestionType.optional(),
    options: QuestionOptionsSchema.optional(),
    required: z.boolean().optional(),
    enabled: z.boolean().optional(),
    priorYearHintDisabled: z.boolean().optional(),
    allowEvidence: z.boolean().optional(),
    allowNote: z.boolean().optional(),
    aiContext: z.string().nullable().optional(),
    order: z.number().int().optional(),
    applicablePeriods: ApplicablePeriodsSchema.optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== "expectedVersionUpdatedAt").length > 0, {
    message: "En az bir alan güncellenmeli",
  })
  // D15 (patch içi): options + allowEvidence aynı patch'te çakışıyorsa reddet.
  // Patch'te yalnızca biri varsa mevcut DB durumuyla çakışma servis katmanında kontrol edilir.
  .refine((v) => !(v.allowEvidence === true && v.options && compositeHasFilePart(v.options)), {
    message: "Dosya parçası içeren bileşik soruda kanıt belgesi açılamaz",
    path: ["allowEvidence"],
  });
export type UpdateQuestionRequest = z.infer<typeof UpdateQuestionRequestSchema>;

export const DeleteRequestSchema = z.object({
  expectedVersionUpdatedAt: z.string().datetime(),
});
export type DeleteRequest = z.infer<typeof DeleteRequestSchema>;
